import {Loader, dataTestId} from '@hconnect/uikit'
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Autocomplete
} from '@mui/material'
import React, {useEffect, useRef} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useAreaDetails} from '../hooks/departmentConfig/useAreaDetails'
import {useProductionLines} from '../hooks/departmentConfig/useProductionLines'
import {useCreateDepartment} from '../hooks/upmEntityConfig/useCreateDepartment'
import {useCreateProductionLine} from '../hooks/upmEntityConfig/useCreateProductionLine'
import {useDepartmentTypesForArea} from '../hooks/upmEntityConfig/useDepartmentTypesForArea'
import {useItemDetails} from '../providers'
import {DepartmentCreateType} from '../types/departmentConfig'

export const AddDepartmentPopup = () => {
  const {
    handleSubmit,
    control,
    formState: {isValid}
  } = useForm<DepartmentCreateType>({
    mode: 'onChange',
    defaultValues: {
      displayValue: '',
      typeId: ''
    }
  })

  const {editedArea, setEditedArea} = useItemDetails()
  const handleClose = () => {
    setEditedArea(null)
  }

  const {data: areaDetails} = useAreaDetails(editedArea?.upmId)
  const {data: productionLines, isLoading: isLoadingProductionLines} = useProductionLines(
    areaDetails?.id
  )
  const {data: departmentTypes, isLoading: isLoadingDept} = useDepartmentTypesForArea(
    areaDetails?.typeId
  )

  const {mutate: createProductionLine, mutateAsync: createProductionLineAsync} =
    useCreateProductionLine()

  const newlyCreatedProductionLineId = useRef('')
  const hasRequestedProductionLine = useRef(false)

  useEffect(() => {
    if (!hasRequestedProductionLine.current && productionLines?.length === 0 && areaDetails?.id) {
      hasRequestedProductionLine.current = true
      createProductionLine(
        {productionCode: '1', areaId: areaDetails.id},
        {
          onSuccess: (response) => {
            newlyCreatedProductionLineId.current = response.id
          }
        }
      )
    }
  }, [productionLines?.length, areaDetails?.id, createProductionLine])

  const {mutate: createDepartment} = useCreateDepartment()

  const {t} = useTranslation()

  const onSubmit = async (data: DepartmentCreateType) => {
    let productionLineId = productionLines?.[0]?.id ?? newlyCreatedProductionLineId.current
    if (!productionLineId && areaDetails?.id) {
      const created = await createProductionLineAsync({productionCode: '1', areaId: areaDetails.id})
      productionLineId = created.id
    }
    createDepartment(
      {
        ...data,
        productionLineId
      },
      {
        onSuccess: () => {
          handleClose()
        }
      }
    )
  }

  const noDepartmentTypes = !departmentTypes || departmentTypes.length === 0
  const isLoading = isLoadingDept || isLoadingProductionLines

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('plantSetup.departmentConfig.addNewDepartment')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {isLoading ? (
            <Loader />
          ) : (
            <Stack gap={2}>
              <Controller
                name="displayValue"
                control={control}
                rules={{required: t('error.required')}}
                render={({field, fieldState}) => (
                  <TextField
                    {...field}
                    label={t('plantSetup.departmentConfig.departmentName')}
                    fullWidth
                    {...dataTestId('department-name-text-field')}
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="typeId"
                control={control}
                rules={{required: t('error.required')}}
                render={({field: {ref, value, onChange}, fieldState}) => (
                  <Autocomplete
                    {...dataTestId('department-type-select')}
                    options={departmentTypes || []}
                    fullWidth
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    value={departmentTypes?.find((dept) => dept.id === value) || undefined}
                    onChange={(_, newValue) => {
                      onChange(newValue?.id ?? null)
                    }}
                    disabled={noDepartmentTypes || isLoadingDept}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('plantSetup.departmentConfig.departmentType')}
                        variant="outlined"
                        inputRef={ref}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            {...dataTestId('save-department-btn')}
            disabled={!isValid || noDepartmentTypes || isLoadingDept}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
